import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Clock,
  PlusCircle,
  ChartColumn,
  List,
  Users,
  FolderOpen,
  LogOut,
  Lightbulb,
  LightbulbOff,
  ClipboardCheck,
  ScrollText,
  Mail,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/atoms/button';
import { Separator } from '../ui/atoms/separator';
import LightSwitch from '../ui/atoms/light-switch';
import { useAuth } from '../../contexts/AuthContext';
import { entriesApi } from '../../api/entries';
import { Permission, PermissionEnum } from '../../types';
import logo from '../../assets/images/Tempus-Text-Light-2.png';
import logoDark from '../../assets/images/Tempus-Text-Dark-2.png';
import finairaLogo from '../../assets/images/FinairaLogo.png';
import finairaLogoDark from '../../assets/images/FinairaLogo-Dark.svg';
import { useCurrentUser } from '../../contexts/CurrentUserContext';
// Create React App lets src import the package's own package.json, so the number shown is the one in it.
import pkg from '../../../package.json';

interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  end?: boolean;
  badge?: number;
}

const employeeLinks: NavItemProps[] = [
  { to: '/employee/entries', icon: <Clock className="h-4 w-4" />, label: 'My Entries', end: true },
  { to: '/employee/entries/new', icon: <PlusCircle className="h-4 w-4" />, label: 'Log Hours' },
];

// Each admin link declares the permission(s) its route actually requires
// (see App.tsx's ProtectedRoute requirePermission props), so the sidebar
// never shows a link the user would just get redirected away from.
interface AdminLinkDef extends NavItemProps {
  isVisible: (hasPermission: (p: Permission) => boolean) => boolean;
}

const adminLinkDefs: AdminLinkDef[] = [
  { to: '/admin/analytics', icon: <ChartColumn className="h-4 w-4" />, label: 'Analytics', isVisible: (has) => has(PermissionEnum.VIEW_ANALYTICS) },
  { to: '/admin/entries', icon: <List className="h-4 w-4" />, label: 'All Entries', isVisible: (has) => has(PermissionEnum.VIEW_ANALYTICS) },
  { to: '/admin/projects', icon: <FolderOpen className="h-4 w-4" />, label: 'Projects', isVisible: (has) => has(PermissionEnum.PROJECT_MANAGEMENT) },
  { to: '/admin/users', icon: <Users className="h-4 w-4" />, label: 'Users', isVisible: (has) => has(PermissionEnum.USER_MANAGEMENT) },
  { to: '/admin/audit-log', icon: <ScrollText className="h-4 w-4" />, label: 'Audit Log', isVisible: (has) => has(PermissionEnum.VIEW_AUDIT_LOG) },
  { to: '/admin/release-notes', icon: <Mail className="h-4 w-4" />, label: 'Release Notes', isVisible: (has) => has(PermissionEnum.IS_MAINTAINER) },
];

function NavItem({ to, icon, label, end, badge }: NavItemProps) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-150',
          isActive
            ? 'text-white shadow-sm'
            : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white',
        )
      }
      style={({ isActive }) =>
        isActive ? { background: 'linear-gradient(135deg, #444ab2 0%, #459fec 100%)' } : {}
      }
    >
      {icon}
      <span className="flex-1">{label}</span>
      {!!badge && badge > 0 && (
        <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-semibold text-white">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </NavLink>
  );
}

export function Sidebar() {
  const { user, hasPermission, logout } = useAuth();
  const { user: me } = useCurrentUser();
  
  const navigate = useNavigate();
  const [isDark, setIsDark] = useState(
    () => document.documentElement.classList.contains('dark'),
  );

  const visibleAdminLinks = adminLinkDefs.filter((link) => link.isVisible(hasPermission));
  const isAdmin = visibleAdminLinks.length > 0;

  // Lightweight check: does this user have anything pending their approval?
  // Doubles as the badge count. Only fetched once per mount, refetched periodically.
  const showApprovals = !!user?.isApprover;
  const { data: approvalsData } = useQuery({
    queryKey: ['approvals-queue-count'],
    queryFn: () => entriesApi.approvalsQueue({ page: 1, limit: 1 }).then((r) => r.data),
    staleTime: 60_000,
    refetchInterval: 60_000,
    enabled: showApprovals,
  });
  const approvalsCount = approvalsData?.meta?.total ?? 0;
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleTheme = () => {
    const next = !isDark;
    setIsDark(next);
    document.documentElement.classList.toggle('dark', next);
    localStorage.setItem('theme', next ? 'dark' : 'light');
  };

  const initials = user?.email?.[0]?.toUpperCase() ?? '?';

  return (
    <aside className="flex h-screen w-60 flex-col border-r border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm dark:shadow-none">
      <div className="flex items-center border-b border-slate-100 dark:border-slate-800 px-5 py-4">
        <img src={logo} alt="Tempus" className="w-[100%] h-auto dark:hidden" />
        <img src={logoDark} alt="Tempus" className="hidden w-[100%] h-auto dark:block" />
      </div>

      <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-4">
        {isAdmin ? (
          <>
            <p className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
              Admin
            </p>
            {visibleAdminLinks.map((link) => (
              <NavItem key={link.to} {...link} />
            ))}
            <Separator className="my-3 dark:bg-slate-800" />
            <p className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
              My Work
            </p>
            {employeeLinks.map((link) => (
              <NavItem key={link.to} {...link} />
            ))}
            {showApprovals && (
              <NavItem
                to="/employee/approvals"
                icon={<ClipboardCheck className="h-4 w-4" />}
                label="Approvals"
                badge={approvalsCount}
              />
            )}
          </>
        ) : (
          <>
            {employeeLinks.map((link) => (
              <NavItem key={link.to} {...link} />
            ))}
            {showApprovals && (
              <NavItem
                to="/employee/approvals"
                icon={<ClipboardCheck className="h-4 w-4" />}
                label="Approvals"
                badge={approvalsCount}
              />
            )}
          </>
        )}
      </nav>

      <div className="border-t border-slate-100 dark:border-slate-800 px-3 py-3 space-y-1">
        {/* Powered by */}
        <div className="flex items-center justify-center gap-2 px-3 py-2">
          <span className="whitespace-nowrap text-[11px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
            Powered by
          </span>
          <img src={finairaLogo} alt="Finaira" className="h-5 w-auto dark:hidden" />
          <img src={finairaLogoDark} alt="Finaira" className="hidden h-5 w-auto dark:block" />
        </div>

        <Separator className="dark:bg-slate-800" />

        {/* Theme toggle */}
        <div className="flex items-center justify-between rounded-lg px-3 py-2">
          <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
            {isDark ? <LightbulbOff className="h-4 w-4" /> : <Lightbulb className="h-4 w-4" />}
            <span>Lights</span>
          </div>
          <LightSwitch checked={!isDark} onCheckedChange={toggleTheme} aria-label="Lights (turn off for dark mode)" />
        </div>

        <Separator className="dark:bg-slate-800" />

        {/* User info */}
        <div className="flex items-center gap-3 rounded-lg px-2 py-2">
          <div
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
            style={{ background: 'linear-gradient(135deg, #444ab2 0%, #459fec 100%)' }}
          >
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-slate-900 dark:text-white">{me?.name}</p>
            <p className="text-xs text-slate-400 dark:text-slate-500">{me?.jobTitle?.name}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start gap-2 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
          onClick={handleLogout}
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </Button>
        <p className="pt-1 text-center text-[11px] text-slate-400 dark:text-slate-500" title={`Tempus version ${pkg.version}`}>
          v{pkg.version}
        </p>
      </div>
    </aside>
  );
}
